import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ImageWithEmptyFallback from "src/components/ImageWithEmptyFallback/ImageWithEmptyFallback";
import { getPointsBeforeConversion } from "src/Modules/PointsExchange/Pages/ProgramExchange/utils";
import { formatNumber } from "src/utility";
import { ExchangeIcon } from "src/assets/Icons";
import { track } from "@amplitude/analytics-browser";

type ExchangePointCardProps = {
  code: string;
  name?: string;
  imageUrl?: string;
  items?: {
    conversionRate?: number;
    fees?: number;
    unitLabel?: string | null;
    from?: number | null;
    to?: number | null;
  }[];
  loading?: boolean;
};

const ExchangePointCard: FC<ExchangePointCardProps> = ({ code, name, imageUrl, items = [], loading }) => {
  const { t } = useTranslation();
  const { conversionRate = 1, fees = 0, unitLabel, from, to } = items[0] ?? {};
  const { sentValue: min } = getPointsBeforeConversion(from ?? 0, conversionRate, fees);
  const { sentValue: max } = getPointsBeforeConversion(to ?? 0, conversionRate, fees);
  const pointLabel = t("PointsExchange.exchange.point");
  const rateFrom = `${formatNumber(1)} ${pointLabel}`;
  const rateTo = `${formatNumber(conversionRate * (1 - fees / 100))} ${unitLabel ?? pointLabel}`;
  const hasRange = from != null && to != null;

  if (loading) {
    return (
      <div className="relative font-inter overflow-hidden rounded-xs bg-background-default p-3 shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="flex aspect-square w-full animate-pulse items-center justify-center overflow-hidden rounded-sm bg-background-subtle">
            <svg className="h-10 w-10 text-icon-subtle" fill="none" viewBox="0 0 24 24" stroke="currentcolor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div className="flex w-full flex-col items-start gap-2">
            <div className="h-5 w-2/3 animate-pulse rounded-sm bg-background-subtle md:h-6" />
            <div className="flex w-full flex-col items-start gap-1">
              <div className="h-3 w-1/3 animate-pulse rounded-sm bg-background-subtle" />
              <div className="h-4 w-3/4 animate-pulse rounded-sm bg-background-subtle md:h-5" />
            </div>
          </div>
          <div className="absolute right-4 top-4 flex items-center gap-0.5 rounded-xs bg-background-hover px-1.5 py-1 md:gap-1 md:px-3 md:py-2">
            <div className="h-2.5 w-7 animate-pulse rounded-sm bg-background-subtle md:h-4 md:w-10" />
            <div className="h-2.5 w-2.5 animate-pulse rounded-full bg-background-subtle md:h-3.5 md:w-3.5" />
            <div className="h-2.5 w-8 animate-pulse rounded-sm bg-background-subtle md:h-4 md:w-12" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Link
      className="relative font-inter group overflow-hidden rounded-xs bg-background-default p-3 shadow-sm transition-shadow hover:shadow-xs"
      to={`exchange-points/${code}`}
      onClick={() => track("Card Selected", { code, name })}
    >
      <div className="flex flex-col items-center justify-center gap-3">
        <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-sm">
          <ImageWithEmptyFallback
            src={imageUrl}
            alt={name}
            className="h-full! w-full! rounded-sm! object-contain transition-transform duration-300 group-hover:scale-105"
          />
        </div>

        <div className="flex w-full flex-col items-start gap-2">
          <div className="text-font-default w-full truncate text-sm font-medium capitalize md:text-base">{name}</div>
          {hasRange && (
            <div className="flex flex-col items-start justify-start">
              <div className="text-font-subtle text-xs leading-tight font-medium">{t("PointsExchange.exchange.pointsRange")}</div>
              <div className="text-font-default text-sm leading-tight md:text-base">
                {formatNumber(min)} - {formatNumber(max)} {t("PointsExchange.exchange.pointsSuffix")}
              </div>
            </div>
          )}
        </div>
        <div
          className="absolute right-4 top-4 flex max-w-3/4 items-center gap-0.5 rounded-xs! bg-background-hover px-1.5 py-1 text-10 font-semibold text-font-active md:gap-1 md:px-3 md:py-2 md:text-xs"
          title={`${t("PointsExchange.exchange.exchangeRate")}: ${rateFrom} ⇄ ${rateTo}`}
        >
          <span className="shrink-0 whitespace-nowrap">{rateFrom}</span>
          <ExchangeIcon className="h-2.5 w-2.5 shrink-0 md:h-3.5 md:w-3.5" />
          <span className="truncate">{rateTo}</span>
        </div>
      </div>
    </Link>
  );
};

export default ExchangePointCard;
